import React from 'react';
import { createRoot } from 'react-dom/client';
import { startOfMinute, endOfMinute } from 'date-fns';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { getReducedLocations, getMetricsRange } from '#component/map/helper';
import DevicesTooltip from './devices-tooltip';
import DeviceTooltip from './device-tooltip';
import { compareMetric } from '#helper';

function getMetricsFromLocations(locations, metaMetrics) {
  const metrics = locations.map(l => l.metrics);
  const sensors = {};
  metaMetrics.forEach(k => {
    sensors[k.identifier] = metrics;
  });
  return sensors;
}

function getFirstAndLastTime(properties) {
  let first = properties.timestamp;
  let last = properties.timestamp;

  if (properties.firstTimestamp) {
    first = properties.firstTimestamp;
    last = properties.lastTimestamp;
  }

  return { first, last };
}

function getDateRange(features) {
  let range = null;
  features.forEach(feature => {
    const r = getFirstAndLastTime(feature.properties);
    if (!range) {
      range = r;
      return false;
    }
    if (r.first < range.first) {
      range.first = r.first;
    }
    if (r.last > range.last) {
      range.last = r.last;
    }
    return true;
  });

  return range;
}

function getClosestSensorMetric(metrics = [], timestamp) {
  return metrics.find((m, i) => {
    const nextMetric = metrics[i + 1];
    if (!nextMetric || nextMetric.timestamp > timestamp) {
      return true;
    }
    return false;
  });
}

function getSensorMetrics(metrics = [], location) {
  if (location.firstTimestamp) {
    return metrics.filter((m, i) => {
      const nextMetric = metrics[i + 1];
      if ((!nextMetric || nextMetric.timestamp >= location.firstTimestamp) &&
        m.timestamp <= location.lastTimestamp) {
        return true;
      }
      return false;
    });
  }
  return [getClosestSensorMetric(metrics, location.timestamp)];
}

function getClosestRange(metrics, metaMetrics, timestamp) {
  let metricsRange = null;
  metaMetrics.forEach(k =>
    k.sensors.forEach(s => {
      const m = getClosestSensorMetric(metrics[k.identifier], timestamp);
      if (m) {
        metricsRange = getMetricsRange(metricsRange, m, s.key);
      }
      /* metrics[k.identifier]?.find((m, i) => {
        const nextMetric = metrics[k.identifier][i + 1];
        if (!nextMetric || nextMetric.timestamp >= timestamp) {
          metricsRange = getMetricsRange(metricsRange, m, s.key);
          return true;
        }
        return false;
      }); */
    }));
  return metricsRange;
}

function getLocationsRange(metrics, metaMetrics, startDate, endDate) {
  let metricsRange = null;
  metaMetrics.forEach(k =>
    k.sensors.forEach(s =>
      metrics[k.identifier]?.forEach((m, i) => {
        const nextMetric = metrics[k.identifier][i + 1];
        if (nextMetric?.timestamp >= startDate && m.timestamp <= endDate) {
          metricsRange = getMetricsRange(metricsRange, m, s.key);
        }
      })));
  return metricsRange;
}

function getDeviceListObject(features, selectedDevices, meta) {
  let devices = {};

  // build the clicked date range per device
  features.forEach(f => {
    const p = f.properties;
    const r = getFirstAndLastTime(p);
    if (!devices[p.uid]) {
      devices[p.uid] = {
        uid: p.uid,
        id: p.deviceId,
        title: p.title,
        type: p.type,
        firstTimestamp: r.first,
        lastTimestamp: r.last,
        tempExcursion: p.tempExcursion,
        humiExcursion: p.humiExcursion,
        batExcursion: p.batExcursion
      };
    }

    if (devices[p.uid].firstTimestamp > r.first) {
      devices[p.uid].firstTimestamp = r.first;
    }

    if (devices[p.uid].lastTimestamp < r.last) {
      devices[p.uid].lastTimestamp = r.last;
    }

    if (!devices[p.uid].tempExcursion && p.tempExcursion) {
      devices[p.uid].tempExcursion = p.tempExcursion;
    }

    if (!devices[p.uid].humiExcursion && p.humiExcursion) {
      devices[p.uid].humiExcursion = p.humiExcursion;
    }

    if (!devices[p.uid].batExcursion && p.batExcursion) {
      devices[p.uid].batExcursion = p.batExcursion;
    }
  });

  devices = Object.values(devices);
  devices.forEach(d => {
    const device = selectedDevices.find(sd => sd.uid === d.uid);
    const metaMetrics = meta.device.find(m => m.name === device.type).metrics ?? [];
    // 1. device.metrics need time to be loaded (isMetricsLoaded)
    // 2. shipments on Dashboard page do not have metrics
    const metrics = device.metrics || getMetricsFromLocations(device.locations, metaMetrics);
    if (d.firstTimestamp === d.lastTimestamp) {
      d.timestamp = d.firstTimestamp;
      d.metricsRange = getClosestRange(
        metrics,
        metaMetrics,
        d.timestamp
      );
      delete d.firstTimestamp;
      delete d.lastTimestamp;
    } else {
      d.metricsRange = getLocationsRange(
        metrics,
        metaMetrics,
        startOfMinute(new Date(d.firstTimestamp)).toISOString(),
        endOfMinute(new Date(d.lastTimestamp)).toISOString()
      );
      // sometimes there aren't any metrics within date range
      if (!d.metricsRange) {
        d.metricsRange = getClosestRange(
          metrics,
          metaMetrics,
          d.firstTimestamp
        );
      }
    }
  });
  return devices;
}

function addPlaceHover() {
  const { map } = this;
  const { mapboxgl } = window;
  const { account, theme, meta } = map.app;
  // Create a popup, but don't add it to the map yet.
  const popup = new mapboxgl.Popup({
    offset: 12,
    closeButton: false,
    closeOnClick: false,
    maxWidth: 320
  });

  const div = document.createElement('div');
  const root = createRoot(div);

  map.on('mouseenter', 'places', e => {
    // Change the cursor style as a UI indicator.
    map.getCanvas().style.cursor = 'pointer';

    // Copy coordinates array.
    const coordinates = e.features[0].geometry.coordinates.slice();
    const properties = { ...e.features[0].properties };
    // properties.metrics = properties.metrics ? JSON.parse(properties.metrics) : {};
    const metrics = getDeviceListObject([{ properties }], map.app.devices, meta);
    const device = { ...properties, ...metrics[0] };
    root.render(
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <DeviceTooltip device={device} account={account} meta={meta} />
        </ThemeProvider>
      </StyledEngineProvider>
    );

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    popup.setLngLat(coordinates).setDOMContent(div).addTo(map);
  });
  map.on('mouseleave', 'places', () => {
    map.getCanvas().style.cursor = '';
    // prevents jumping on load
    setTimeout(() => root.unmount());
    popup.remove();
  });
}

function addClusterHover() {
  const { map } = this;
  const { mapboxgl } = window;
  const { theme } = map.app;
  // Create a popup, but don't add it to the map yet.
  const popup = new mapboxgl.Popup({
    offset: 12,
    closeButton: false,
    closeOnClick: false,
    maxWidth: 320
  });

  const div = document.createElement('div');
  const root = createRoot(div);

  map.on('mouseenter', 'clusters', e => {
    // Change the cursor style as a UI indicator.
    map.getCanvas().style.cursor = 'pointer';

    // Copy coordinates array.
    const coordinates = e.features[0].geometry.coordinates.slice();
    const { properties, id: featureId } = e.features[0];

    const sourceObject = map.getSource('locations');
    sourceObject.getClusterLeaves(
      featureId,
      e.features[0].properties.count,
      0,
      (error, features) => {
        if (!error) {
          const dateRange = getDateRange(features);

          root.render(
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <DevicesTooltip
                  properties={properties}
                  devices={map.app.devices}
                  dateRange={dateRange}
                  account={map.app.account}
                />
              </ThemeProvider>
            </StyledEngineProvider>
          );

          // Ensure that if the map is zoomed out such that multiple
          // copies of the feature are visible, the popup appears
          // over the copy being pointed to.
          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }

          // Populate the popup and set its coordinates
          // based on the feature found.
          const { count: c } = properties;
          const offset = c >= 750 ? 30 : c >= 100 ? 26 : c >= 10 ? 18 : 12;
          popup.setOffset(offset);
          popup.setLngLat(coordinates).setDOMContent(div).addTo(map);
        }
      }
    );
  });
  map.on('mouseleave', 'clusters', () => {
    map.getCanvas().style.cursor = '';
    // prevents jumping on load
    setTimeout(() => root.unmount());
    popup.remove();
  });
}

function handleClicked(map, featureId) {
  const cl = map.app.clickedLocations;
  const button = map.app.resetClickedLocationButton;
  const wasClicked = cl.indexOf(featureId);
  if (wasClicked === -1) {
    cl.push(featureId);
  } else {
    cl.splice(wasClicked, 1);
  }

  if (cl.length) {
    if (!map.hasControl(button)) map.addControl(button);
  } else {
    map.removeControl(button);
  }
}

function addPlaceClick() {
  const { map } = this;
  map.on('click', 'places', e => {
    const feature = e.features[0];
    const device = feature.properties;
    const featureId = e.features[0].id;
    map.setFeatureState(
      { source: 'locations', id: featureId },
      { clicked: !e.features[0].state.clicked }
    );
    handleClicked(map, featureId);

    map.app.helper.setClickedDevices(
      getDeviceListObject(
        [{
          ...feature,
          properties: { ...device, metrics: device.metrics ? JSON.parse(device.metrics) : {} }
        }],
        map.app.devices,
        map.app.meta
      )
    );
  });
}

function addClusterClick() {
  const { map } = this;
  map.on('click', 'clusters', e => {
    // deeplink page
    if (map.app.helper.setClickedDevices) {
      const featureId = e.features[0].id;
      map.setFeatureState(
        { source: 'locations', id: featureId },
        { clicked: !e.features[0].state.clicked }
      );
      handleClicked(map, featureId);

      const sourceObject = map.getSource('locations');
      sourceObject.getClusterLeaves(
        featureId,
        e.features[0].properties.count,
        0,
        (error, features) => {
          if (!error) {
            map.app.helper.setClickedDevices(
              getDeviceListObject(features, map.app.devices, map.app.meta)
            );
          }
        }
      );
    }
  });
}

function addClusterProperties(geojson, devices) {
  const clusterProperties = {};
  devices.forEach(d => {
    const ifUidMatch = ['==', d.uid, ['get', 'uid']];
    clusterProperties[d.uid] = ['+', ['case', ifUidMatch, 1, 0]];
    clusterProperties.lastLocation = ['+', ['case', ['==', ['get', 'lastLocation'], 1], 1, 0]];
    clusterProperties.firstLocation = ['+', ['case', ['==', ['get', 'firstLocation'], 1], 1, 0]];
    clusterProperties.tempExcursion = ['+', ['case', ['==', ['get', 'tempExcursion'], 1], 1, 0]];
    clusterProperties.humiExcursion = ['+', ['case', ['==', ['get', 'humiExcursion'], 1], 1, 0]];
    clusterProperties.batExcursion = ['+', ['case', ['==', ['get', 'batExcursion'], 1], 1, 0]];
    const ifTempExc = ['>=', ['get', 'tempExcursion'], 1];
    clusterProperties.tempExcursionUids = ['concat', ['case', ifTempExc, ['concat', ['get', 'uid'], '|'], '']];
    const ifHumiExc = ['>=', ['get', 'humiExcursion'], 1];
    clusterProperties.humiExcursionUids = ['concat', ['case', ifHumiExc, ['concat', ['get', 'uid'], '|'], '']];
    const ifBatExc = ['>=', ['get', 'batExcursion'], 1];
    clusterProperties.batExcursionUids = ['concat', ['case', ifBatExc, ['concat', ['get', 'uid'], '|'], '']];
    clusterProperties.lat = ['max', ['get', 'lat'], ['accumulated']];
    clusterProperties.radius = ['max', ['get', 'radius'], ['accumulated']];
    clusterProperties.count = ['+', ['get', 'count']];
  });
  geojson.clusterProperties = clusterProperties;
  return geojson;
}

function getGeoJSON({ devices: data, destinations }, defaultMapCenter) {
  let locationsBound = null;
  let lastLocationBound = null;

  const devices = data.reduce((a, c) => {
    if (c.locationsChunks) {
      c.locationsChunks.forEach(locations => {
        a.push({ ...c, locations });
      });
    } else {
      a.push(c);
    }

    return a;
  }, []);

  const features = [];
  devices.forEach(d => {
    const { projectAlertId } = d;
    const alertSetting = projectAlertId ? d.alerts.find(a => a.id === projectAlertId) : null;
    const { tMin, tMax, hMin, hMax, bMin } = alertSetting || {};
    const locations = getReducedLocations(d, 0.1);

    locations.forEach((l, i) => {
      const firstLocation = Number(i === 0);
      const lastLocation = Number(i === locations.length - 1);
      if (!locationsBound) {
        locationsBound = [l.lng, l.lat, l.lng, l.lat];
      } else {
        if (l.lng < locationsBound[0]) locationsBound[0] = l.lng;
        if (l.lat < locationsBound[1]) locationsBound[1] = l.lat;
        if (l.lng > locationsBound[2]) locationsBound[2] = l.lng;
        if (l.lat > locationsBound[3]) locationsBound[3] = l.lat;
      }

      l.tempExcursion = 0;
      l.humiExcursion = 0;
      l.batExcursion = 0;
      if (alertSetting && l.metrics) {
        if (typeof (tMin ?? tMax) === 'number') {
          const metrics = d.metrics?.temperature?.length ?
            getSensorMetrics(d.metrics.temperature, l) : l.groupedMetrics;
          l.tempExcursionMetric = metrics.find(m => {
            const value = m.temp_probe ?? m.temp ?? m.t_in;
            return compareMetric(tMin, tMax, value);
          });
          l.tempExcursion = l.tempExcursionMetric ? 1 : 0;
        }
        if (typeof (hMin ?? hMax) === 'number') {
          const metrics = d.metrics?.humidity?.length ?
            getSensorMetrics(d.metrics.humidity, l) : l.groupedMetrics;
          l.humiExcursionMetric = metrics.find(m => compareMetric(hMin, hMax, m.humi));
          l.humiExcursion = l.humiExcursionMetric ? 1 : 0;
        }
        if (typeof bMin === 'number') {
          const metrics = d.metrics?.battery?.length ?
            getSensorMetrics(d.metrics.battery, l) : l.groupedMetrics;
          l.batExcursionMetric = metrics.find(m => compareMetric(bMin, null, m.bat));
          l.batExcursion = l.batExcursionMetric ? 1 : 0;
        }
      }

      l.color = d.color;
      l.uid = d.uid;
      l.title = d.title;
      l.type = d.type;
      l.deviceId = d.id;
      l.lastLocation = lastLocation;
      l.firstLocation = firstLocation;
      features.push({
        type: 'Feature',
        properties: l,
        geometry: {
          type: 'Point',
          coordinates: [l.origin.lng, l.origin.lat]
        }
      });
      if (lastLocation) {
        if (!lastLocationBound) {
          lastLocationBound = [l.origin.lng, l.origin.lat, l.origin.lng, l.origin.lat];
        } else {
          if (l.origin.lng < lastLocationBound[0]) lastLocationBound[0] = l.origin.lng;
          if (l.origin.lat < lastLocationBound[1]) lastLocationBound[1] = l.origin.lat;
          if (l.origin.lng > lastLocationBound[2]) lastLocationBound[2] = l.origin.lng;
          if (l.origin.lat > lastLocationBound[3]) lastLocationBound[3] = l.origin.lat;
        }
      }
    });
  });

  if (destinations) {
    destinations.forEach(d => {
      if (d.locations) {
        const l = d.locations[0];
        if (!locationsBound) {
          locationsBound = [l.lng, l.lat, l.lng, l.lat];
        } else {
          if (l.lng < locationsBound[0]) locationsBound[0] = l.lng;
          if (l.lat < locationsBound[1]) locationsBound[1] = l.lat;
          if (l.lng > locationsBound[2]) locationsBound[2] = l.lng;
          if (l.lat > locationsBound[3]) locationsBound[3] = l.lat;
        }
      }
    });
  }

  if (!locationsBound) {
    const [lng, lat] = defaultMapCenter;
    locationsBound = [lng, lat, lng, lat];
  }

  return {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features
    },
    generateId: true,
    cluster: true,
    clusterMaxZoom: 18,
    clusterRadius: 20,
    locationsBound,
    devices: data,
    lastLocationBound
  };
}

export default {
  addLayer(map) {
    this.map = map;
    map.addLayer({
      id: 'locations-radius',
      type: 'circle',
      source: 'locations',
      paint: {
        'circle-color': ['case',
          ['==', ['feature-state', 'clicked'], true],
          [
            'case',
            [
              'any',
              ['>=', ['get', 'tempExcursion'], 1],
              ['>=', ['get', 'humiExcursion'], 1],
              ['>=', ['get', 'batExcursion'], 1]
            ],
            '#f44336',
            '#5eb676'],
          'transparent'],
        'circle-radius': [
          'interpolate',
          ['exponential', 2],
          ['zoom'],
          0, 0,
          // https://stackoverflow.com/questions/37599561/drawing-a-circle-with-the-radius-in-miles-meters-with-mapbox-gl-js
          // 78271.484 / 2 ** zoom. For example: 78271.484 / 2 ** 20 = 0.07464550399780273
          // ['/', 78271.484, ['^', 2, 20]]
          // https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#Resolution_and_Scalehttps://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#Resolution_and_Scale
          // 78271.484 meters/pixel * cos(latitude) / (2 ^ zoomlevel) = 0.0731462458862654
          // ['*', 78271.484, ['/', ['cos', ['get', 'lat']], ['^', 2, 20]]]
          20, ['/',
            ['/', ['get', 'radius'], ['/', 78271.484, ['^', 2, 20]]],
            ['cos', ['*', ['get', 'lat'], ['/', Math.PI, 180]]]
          ]],
        'circle-opacity': 0.5
      }
    });

    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'locations',
      filter: ['has', 'cluster'],
      paint: {
        'circle-color': [
          'case',
          [
            'any',
            ['>=', ['get', 'tempExcursion'], 1],
            ['>=', ['get', 'humiExcursion'], 1],
            ['>=', ['get', 'batExcursion'], 1]
          ],
          '#f44336',
          '#5eb676'
        ],
        'circle-radius': [
          'step',
          ['get', 'count'],
          10,
          10,
          18,
          100,
          26,
          750,
          30
        ]
      }
    });

    map.addLayer({
      id: 'places',
      // type: 'symbol',
      type: 'circle',
      source: 'locations',
      filter: ['!=', 'cluster', true],
      paint: {
        // 'circle-color': ['concat', '#', ['get', 'color']],
        'circle-color': [
          'case',
          [
            'any',
            ['>=', ['get', 'tempExcursion'], 1],
            ['>=', ['get', 'humiExcursion'], 1],
            ['>=', ['get', 'batExcursion'], 1]
          ],
          '#f44336',
          '#5eb676'
        ],
        'circle-radius': 5,
        'circle-stroke-width': 2,
        'circle-stroke-color': [
          'case',
          ['any',
            ['>=', ['get', 'tempExcursion'], 1],
            ['>=', ['get', 'humiExcursion'], 1],
            ['>=', ['get', 'batExcursion'], 1]
          ],
          '#a01308',
          '#417f52'
        ]
      }
      /* layout: {
        'icon-image': ['get', 'type']
      },
      paint: {
        'icon-color': theme.palette.secondary.main,
        'icon-halo-color': theme.palette.secondary.main,
        'icon-halo-width': 2
      } */
    });
  },
  addFlagLayer(map) {
    map.addLayer({
      id: 'locations-first-and-current',
      type: 'symbol',
      source: 'locations',
      filter: ['any', ['>=', 'firstLocation', 1], ['>=', 'lastLocation', 1]],
      layout: {
        'icon-image': ['case',
          ['all', ['>=', ['get', 'firstLocation'], 1], ['==', ['get', 'lastLocation'], 0]],
          'map-flag-start',
          ['all', ['==', ['get', 'firstLocation'], 0], ['>=', ['get', 'lastLocation'], 1]],
          'map-flag-current',
          'map-flag-start-and-current'
        ],
        'icon-offset': [33, -33],
        'icon-allow-overlap': true
      }
    });
  },
  getGeoJSON,
  // memoDrawClusters,
  addClusterProperties,
  addPlaceHover,
  addPlaceClick,
  addClusterHover,
  addClusterClick
};
